import React, { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { AdminMember, AdminOrganization } from "src/Types/auth.types";
import { formatDate } from "./format";

const ALL_CITIES = "__all__";

interface AccountsTableProps {
  members: AdminMember[];
  organizations: AdminOrganization[];
}

const AccountsTable: React.FC<AccountsTableProps> = ({
  members,
  organizations,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [cityId, setCityId] = useState(ALL_CITIES);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return members.filter((member) => {
      if (cityId !== ALL_CITIES && member.organization?.id !== cityId) {
        return false;
      }
      if (!needle) return true;
      return [member.name, member.email, member.organization?.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [members, query, cityId]);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField
          size="small"
          label={t("admin.accounts.search")}
          placeholder={t("admin.accounts.searchPlaceholder")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ flex: 1, maxWidth: { sm: 360 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" aria-hidden="true" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label={t("admin.accounts.city")}
          value={cityId}
          onChange={(event) => setCityId(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value={ALL_CITIES}>{t("admin.accounts.allCities")}</MenuItem>
          {organizations.map((organization) => (
            <MenuItem key={organization.id} value={organization.id}>
              {organization.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Typography variant="body2" color="text.secondary" aria-live="polite">
        {t("admin.accounts.count", { shown: filtered.length, count: members.length })}
      </Typography>

      {filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography color="text.secondary">
            {members.length === 0
              ? t("admin.accounts.emptyAll")
              : t("admin.accounts.emptyFiltered")}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table aria-label={t("admin.accounts.tableLabel")}>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.columns.name")}</TableCell>
                <TableCell>{t("admin.columns.email")}</TableCell>
                <TableCell>{t("admin.accounts.city")}</TableCell>
                <TableCell>{t("admin.columns.keyUsed")}</TableCell>
                <TableCell>{t("admin.columns.registered")}</TableCell>
                <TableCell>{t("admin.columns.lastLogin")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{member.name || "—"}</TableCell>
                  <TableCell sx={{ wordBreak: "break-all" }}>{member.email}</TableCell>
                  <TableCell>{member.organization?.name || "—"}</TableCell>
                  <TableCell>
                    {member.registrationCode
                      ? member.registrationCode.label || t("admin.untitledKey")
                      : "—"}
                  </TableCell>
                  <TableCell>{formatDate(member.createdAt)}</TableCell>
                  <TableCell>{formatDate(member.lastLoginAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default AccountsTable;
